/**
 * followUser — the Follow button follows, and unfollows.
 *
 * WHAT IT DID (app.js:5199):
 *   el.textContent = "Following ✓"; toast("Following @" + handle);
 * No call, no state. The label reverts on the next repaint and the other person
 * is never followed — so the claim is false twice over: nothing happened, and
 * the evidence of it happening disappears on its own.
 *
 * 🔴 THIS ONE WAS NEVER CARDED, AND THAT IS A FINDING ABOUT THE GUARD.
 * `[data-follow]` appears in neither GUARDED_WRITES nor the deliberately-not-
 * guarded list. demoWriteGuardDerivation enumerates handlers that write to
 * SHARED STATE — the data arrays — and this one writes only to `el.textContent`.
 * So the derivation has a blind spot for DOM-ONLY handlers that make a false
 * claim: no array is touched, nothing to enumerate, and the toast still says a
 * thing that did not happen. This is the first found. There may be others, and
 * the instrument cannot see them.
 *
 * THE WIRE. Three endpoints, all real:
 *   POST   /api/users/:id/follow    -> { ok: true, following: true }
 *   DELETE /api/users/:id/follow    -> { ok: true, following: false }
 *   GET    /api/users/:id/follow-status -> { following: boolean }
 * POST is idempotent: an existing follow returns following:true rather than
 * erroring (users.ts:817), so a double-tap cannot break the state. It refuses
 * self-follow with a 400.
 *
 * 🪤 THE ENDPOINTS TAKE AN ID AND HIS MARKUP CARRIES A HANDLE, and there is no
 * lookup to bridge them: GET /api/users/:id matches on id only (users.ts:706),
 * with no by-username route anywhere. So resolution is the handle->id map
 * installFloorListings already returns and ratingFixups already holds. It is
 * built from the floor's sellers and GROWS as comment authors load, which
 * covers the path that matters: you reach a profile by tapping a seller or a
 * commenter, and both are in it by the time their profile paints.
 *
 * A handle that is NOT in the map cannot be followed, and this REFUSES rather
 * than pretending. That is the whole point — a button that silently does
 * nothing is the bug being fixed, so a button that cannot work has to say so.
 */

import { signedInOrWall } from "./guestWall";
import { awaitTap, createClock, isNoAnswer, readIsWayBack, settleOutranksRead, type Read } from "./instantTap";

/** His control: profile (app.js:1408), deal card (:2120), list rows (:3188). */
/**
 * 🔴 ADDED 2026-08-28: THE INITIAL STATE. The owner tested and reported it
 * precisely: "Follow worked upon first try... though upon hard refresh of the
 * page it did not persist." The follow WAS recorded — this was never a write
 * bug. `following` starts empty on every load, so a page that opens straight
 * on someone you already follow paints "Follow" until you click it, which
 * reads exactly like the button forgetting.
 *
 * 🔑 THIS GAP IS NOT THE SAME ONE CARDED FOR NICK. The price vote and the like
 * cannot know the viewer's own state because GET /api/listings carries none —
 * that is a genuine hole in the list route with no single-item fallback in
 * reach. Follow is different: GET /api/users/:id/follow-status exists
 * (users.ts:800, optionalAuth) and answers exactly the question this needs.
 * So this is fixed here, not carded.
 *
 * Fetched on the PROFILE ROUTE only (#/u/:handle) — same shape as
 * installLiveListingComments' currentListingId/maybeFetch, one call per handle
 * per session, triggered on hashchange and once at install to cover landing
 * directly on the route before any hashchange fires. */
export const FOLLOW_SELECTOR = "[data-follow]";

export const SIGNIN_MESSAGE = "Sign in to follow people";
/** The handle resolved to no id — see the header. Never a silent no-op. */
export const UNKNOWN_USER_MESSAGE = "Couldn't find that account — open their profile and try again";
export const GENERIC_FAILURE_MESSAGE = "Couldn't update that follow — please try again";
/** After the budget ran out and the status read could not say either way. */
export const UNCONFIRMED_MESSAGE = "Couldn't confirm that follow. Check the button before tapping again.";
/** His own label for a followed person (app.js). */
export const FOLLOWING_LABEL = "Following ✓";

interface FollowWindow extends Window {
  toast?: (message: string) => void;
}

export function installFollowUser(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /** handle -> users.id, the SAME live object installFloorListings returns and
   *  installLiveListingComments mutates as authors arrive. Held by reference,
   *  never copied, so entries added after boot are visible here too. */
  userIds: Record<string, string>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as FollowWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  const busy = new Set<string>();
  /** Who we know we are following, this session. His markup has no such field. */
  const following = new Set<string>();
  /** Handles whose initial follow-status has already been fetched or is in
   *  flight — never re-fetched, same as installLiveListingComments' `fetched`. */
  const statusChecked = new Set<string>();
  /* ── THE ORDER OF EVENTS (instantTap.ts header): one clock for this module. ── */
  const tick = createClock();
  /** follow-status as last read, per handle: what it said, when it was sent, when it landed. */
  const status = new Map<string, Read<boolean>>();
  /** Presses out right now, by handle: the state each one painted. Set at the paint, cleared at
      the settle or the put-back. */
  const pending = new Map<string, boolean>();
  /** The last settled state per handle (an answer or a status read), and when. */
  const settled = new Map<string, { following: boolean; at: number }>();
  /** The latest press per person. A late answer for an older press changes nothing. */
  const presses = new Map<string, number>();

  function currentProfileHandle(): string | null {
    const m = /^#\/u\/(.+)$/.exec(win.location.hash);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * Best-effort, like every other lazy installer: a failure here leaves the
   * button reading "Follow" even if the visitor already follows this person —
   * wrong, but the SAME wrong as before this fetch existed, never a false
   * positive. It never claims a follow that is not confirmed.
   */
  function checkInitialStatus(): void {
    const handle = currentProfileHandle();
    if (!handle || statusChecked.has(handle)) return;
    const id = userIds[handle];
    if (!id) return; // not yet resolvable — the click-time refusal covers this handle if it stays that way
    statusChecked.add(handle);
    const sentAt = tick();
    authFetch(`/api/users/${encodeURIComponent(id)}/follow-status`)
      .then((res) => (res.ok ? (res.json() as Promise<{ following?: boolean }>) : Promise.resolve(null)))
      .then((body) => {
        if (!body || typeof body.following !== "boolean") return;
        status.set(handle, { value: body.following, sentAt, landedAt: tick() });
        /* The check paints nothing over a press in flight (its answer is the newer
           truth; pass 2, finding 5) or over a settle newer than the check's send. */
        if (pending.has(handle) || settleOutranksRead(settled.get(handle)?.at, sentAt)) return;
        if (!body.following) return;
        following.add(handle);
        applyLabels();
      })
      .catch(() => {
        /* Leaves statusChecked marked rather than retrying — a broken network
           call is not a reason to hammer the endpoint on every repaint. */
      });
  }

  /**
   * 🔴 RE-APPLY AFTER EVERY REPAINT, because setting the label once is not
   * enough and the owner's bug report proved it.
   *
   * renderPublicProfile paints a STATIC `>Follow</button>` (app.js:1408) with
   * no notion of follow state, and his bundle replaces #app wholesale on every
   * render. So an imperatively-set label survives exactly until the next one —
   * from a hashchange, another interceptor, or his own handlers — and then the
   * button silently reads Follow again over a follow that really happened.
   *
   * 🪤 THE FIRST FIX WAS TO STOP CALLING repaint() OURSELVES. That was not
   * enough and is worth recording: it only removed OUR repaint, and the live
   * test still failed because a queued hashchange repainted anyway. The label
   * cannot be written once, it has to be re-asserted — which is the same
   * conclusion demoTextFixups reached about every other correction to his
   * markup.
   *
   * One button, painted on or off. His markup has two resting labels for the
   * same control ("Follow" on a profile, "+ Follow" on a collector card), so the
   * label is kept on the button the first time it is painted on and restored,
   * not replaced by a constant, on the way off. Idempotent: a button already in
   * the state is left alone, so the observer's own mutations cannot feed back.
   */
  function paintButton(el: HTMLElement, on: boolean): void {
    if (on) {
      if (el.dataset.followstate === "on") return;
      if (el.dataset.followlabel === undefined) el.dataset.followlabel = el.textContent ?? "Follow";
      el.dataset.followstate = "on";
      el.textContent = FOLLOWING_LABEL;
    } else if (el.dataset.followstate === "on") {
      /* 🔴 THE EXPLICIT OFF PATH (card 1219177901097382). This used to only ever
         add, so an unfollow left every OTHER button for the person saying
         Following, and the next tap on one of them read "on" from the DOM and
         sent a second unfollow. */
      el.dataset.followstate = "off";
      el.textContent = el.dataset.followlabel ?? "Follow";
    }
  }
  const buttons = (): HTMLElement[] => Array.from(win.document.querySelectorAll<HTMLElement>(FOLLOW_SELECTOR));

  /** Every button for this person on screen, from one state. */
  function paintFollow(handle: string, on: boolean): void {
    for (const el of buttons()) if (el.dataset.follow === handle) paintButton(el, on);
  }

  /** The Set and every button on screen, from one state. */
  function show(handle: string, on: boolean): void {
    if (on) following.add(handle);
    else following.delete(handle);
    paintFollow(handle, on);
  }

  /** Every button on screen matches the Set, in one pass (the Collections page draws up to 99 of them). */
  function applyLabels(): void {
    for (const el of buttons()) {
      const handle = el.dataset.follow;
      if (handle) paintButton(el, following.has(handle));
    }
  }

  /**
   * The id for a handle, or null.
   *
   * 📌 Reads BOTH the shared map and any id the markup happens to carry. Rows
   * in the followers/following modal come straight off the server, which gives
   * ids directly — so where one is present it beats the map, and the map is the
   * fallback for his own Follow buttons which carry only a handle.
   */
  function resolveId(el: HTMLElement, handle: string): string | null {
    return el.dataset.followid ?? userIds[handle] ?? null;
  }

  /** The press, settled: the state is the server's; the guess is no longer on screen. */
  function settle(handle: string, on: boolean): void {
    settled.set(handle, { following: on, at: tick() });
    pending.delete(handle);
    show(handle, on);
    notify(on ? `Following @${handle}` : `Unfollowed @${handle}`);
  }

  /** The press, put back: the guess is no longer on screen. */
  function putBack(handle: string, back: () => boolean): void {
    pending.delete(handle);
    show(handle, back());
  }

  async function apply(handle: string, want: boolean, res: Response, back: () => boolean, still: () => boolean = () => true): Promise<void> {
    if (res.status === 401) {
      putBack(handle, back);
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* Put back, then the server's words. 400 is "Cannot follow yourself" —
         worth quoting rather than flattening, because it tells the person their
         tap was never going to work — and 429 is the limiter's sentence, one
         more refusal handled like the rest (decision 1: nothing here reads its
         number). */
      const b = back();
      putBack(handle, back);
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!still()) return;
      /* No words when the way back already shows what was asked (pass 4, finding 2). */
      if (b === want) return;
      notify(body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }
    /* The response carries the resulting state; trust it over our own guess,
       since POST is idempotent and answers following:true for an existing
       follow rather than erroring. */
    const body = (await res.json().catch(() => ({}))) as { following?: boolean };
    if (!still()) return;
    settle(handle, body.following ?? want);
  }

  /**
   * After the budget ran out: follow-status, read, says whether the write
   * landed. `stale` is true once the original request answered after all;
   * checked AFTER the body is read, because a late answer can land between
   * this read's headers and its body.
   */
  async function reread(handle: string, id: string, want: boolean, stale: () => boolean): Promise<void> {
    const outcome = await awaitTap(authFetch(`/api/users/${encodeURIComponent(id)}/follow-status`));
    const body = outcome.kind === "answer" && outcome.res.ok
      ? ((await outcome.res.json().catch(() => ({}))) as { following?: unknown })
      : {};
    if (stale()) return;
    if (typeof body.following !== "boolean") {
      notify(UNCONFIRMED_MESSAGE);
      return;
    }
    if (body.following === want) settle(handle, body.following);
    else {
      settled.set(handle, { following: body.following, at: tick() });
      pending.delete(handle);
      show(handle, body.following);
      notify(GENERIC_FAILURE_MESSAGE);
    }
  }

  async function toggle(el: HTMLElement, handle: string, id: string): Promise<void> {
    const before = following.has(handle) || el.dataset.followstate === "on";
    const want = !before;
    /* 🔴 PAINTED FROM THE TAP (card 1219177901097382): every button for the
       person moves now; the words wait for the answer (a claim in words before
       the server agreed is the defect this file was written against). */
    show(handle, want);
    const paintedAt = tick();
    pending.set(handle, want);
    const press = (presses.get(handle) ?? 0) + 1;
    presses.set(handle, press);
    /* Busy holds the control for the whole press, so only a LATE answer (after the
       press is over) has to ask whether a newer press has taken the control. */
    const current = (): boolean => presses.get(handle) === press;
    /* The way back: what follow-status said for THIS person when that read is the
       way back (it landed since the paint and was sent after this person's last
       settle), else the tap-time state. Another person's check landing meanwhile
       says nothing about this one (pass 2, finding 2). */
    const back = (): boolean => {
      const s = settled.get(handle);
      /* This press's own status read settled while a late answer was still out (pass 3, finding 2). */
      if (s && s.at > paintedAt) return s.following;
      const read = status.get(handle);
      return readIsWayBack(read, paintedAt, s?.at) ? read!.value : before;
    };

    const request = authFetch(`/api/users/${encodeURIComponent(id)}/follow`, {
      method: want ? "POST" : "DELETE",
    });
    const outcome = await awaitTap(request);
    if (outcome.kind === "answer") {
      await apply(handle, want, outcome.res, back);
      return;
    }
    const b = back();
    putBack(handle, back);
    if (outcome.kind === "thrown") {
      if (b !== want) notify(GENERIC_FAILURE_MESSAGE);
      return;
    }
    let answered = false;
    void request.then(
      (res) => {
        /* A late 502/504 says nothing about the write: the status read decides (pass 5). */
        if (isNoAnswer(res.status)) return;
        answered = true;
        if (current()) void apply(handle, want, res, back, current);
      },
      () => { /* failed after the budget: the status read is the truth */ },
    );
    await reread(handle, id, want, () => answered);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(FOLLOW_SELECTOR) as HTMLElement | null;
    if (!el) return;

    const handle = el.dataset.follow;
    if (!handle) return;
    /* A guest gets the sign-in wall and no request — see guestWall.ts. First,
       so not even the unknown-handle refusal below can toast at a guest. */
    if (!signedInOrWall(win)) return;
    if (busy.has(handle)) return;

    const id = resolveId(el, handle);
    if (!id) {
      /* 🔴 REFUSE OUT LOUD. A handle with no id cannot be followed, and the
         defect being fixed here is precisely a button that appears to work and
         does not. Saying nothing would reproduce it. */
      notify(UNKNOWN_USER_MESSAGE);
      return;
    }

    busy.add(handle);
    void toggle(el, handle, id)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(handle));
  };

  /* Capture, so this sees the click before his target-phase listener. Not
     stopping propagation: [data-follow] joins GUARDED_WRITES in this change, and
     the guard is what suppresses his "Following ✓" label write and his toast —
     both of which would otherwise claim the follow synchronously, before the
     request resolves. Same reasoning as [data-vote]. */
  win.document.addEventListener("click", onClick, true);
  win.addEventListener("hashchange", checkInitialStatus);
  checkInitialStatus(); // covers landing directly on #/u/:handle at boot

  /* Same posture as demoTextFixups: his bundle repaints constantly, so the
     correction has to be re-applied rather than applied. Scoped to #app, the
     node his render actually replaces. */
  const observer = new MutationObserver(applyLabels);
  const root = win.document.getElementById("app") ?? win.document.body;
  if (root) observer.observe(root, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
    win.document.removeEventListener("click", onClick, true);
    win.removeEventListener("hashchange", checkInitialStatus);
  };
}
